import { Box, Spinner } from '@chakra-ui/react'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { CachePolicies, useFetch } from 'use-http'
import OtpVerificationModal, {
  ElevationOperation,
} from '../components/OtpVerificationModal'
import useToast from '../hooks/useToast'
import { UserContext } from './UserProvider'

interface ElevationContextValue {
  /*
      Ensures the session is elevated before performing a sensitive operation.
      Call this FIRST, on the action click, then run the original action
      (reveal / open the roll/delete/create form) only if it resolves `true`.
      - Resolves `true` immediately if the session already holds a valid window.
      - Otherwise triggers an OTP email and opens the reusable OTP modal,
        resolving `true` once verified or `false` if the user cancels.
  */
  ensureElevated(operation: ElevationOperation): Promise<boolean>
}

export const ElevationContext = createContext<ElevationContextValue>({
  ensureElevated: async () => false,
})

export const useElevation = (): ElevationContextValue =>
  useContext(ElevationContext)

// Delay before the "checking access" spinner appears, so the common
// already-elevated (fast) path never flickers a spinner.
const SPINNER_DELAY_MS = 250

const ElevationProvider = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useContext(UserContext)
  const clientNumber = user?.clientNumber

  const { request } = useFetch({ cachePolicy: CachePolicies.NO_CACHE })

  const [modalState, setModalState] = useState<{
    isOpen: boolean
    operation: ElevationOperation
  }>({ isOpen: false, operation: 'reveal' })

  // `checking` is set for the whole initiate round-trip; `showSpinner` only
  // flips true if it stays checking past SPINNER_DELAY_MS (avoids flicker).
  const [checking, setChecking] = useState(false)
  const [showSpinner, setShowSpinner] = useState(false)

  const resolverRef = useRef<((elevated: boolean) => void) | null>(null)

  useEffect(() => {
    if (!checking) {
      setShowSpinner(false)
      return
    }
    const timer = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS)
    return () => clearTimeout(timer)
  }, [checking])

  const settle = useCallback((elevated: boolean) => {
    setModalState(prev => ({ ...prev, isOpen: false }))
    if (resolverRef.current) {
      resolverRef.current(elevated)
      resolverRef.current = null
    }
  }, [])

  const ensureElevated = useCallback(
    async (operation: ElevationOperation): Promise<boolean> => {
      if (!clientNumber) return false

      setChecking(true)
      try {
        const response = (await request.post(
          `/api/clients/${clientNumber}/settings/developer/elevation/initiate`,
          { operation },
        )) as { elevated?: boolean }

        if (request.error) {
          toast({
            description: t(
              'elevation_check_failed',
              'Could not verify your session. Please try again.',
            ),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
          return false
        }

        // Already elevated within the 10-minute window: skip the OTP step.
        if (response?.elevated) return true

        // OTP emailed: open the modal and resolve once verified/cancelled.
        // (`finally` clears the spinner as the OTP modal takes over.)
        return new Promise<boolean>(resolve => {
          resolverRef.current = resolve
          setModalState({ isOpen: true, operation })
        })
      } finally {
        setChecking(false)
      }
    },
    [clientNumber, request, toast, t],
  )

  return (
    <ElevationContext.Provider value={{ ensureElevated }}>
      {children}
      {showSpinner ? (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1500}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="blackAlpha.300"
        >
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="appPaleGrey"
            color="appBlue"
            size="xl"
          />
        </Box>
      ) : null}
      <OtpVerificationModal
        isOpen={modalState.isOpen}
        operation={modalState.operation}
        clientNumber={clientNumber}
        onVerified={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ElevationContext.Provider>
  )
}

export default ElevationProvider
